import { Fragment, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { OperatorOrgSummary } from '@lyra/shared';
import { Copy, Check, ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { DocTop } from '@/components/shell/DocTop';
import { Section } from '@/components/ui/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusLabel } from '@/components/ui/StatusLabel';
import { formatCurrencyEurRo, formatDateTimeRo, formatNumberRo } from '@/lib/format';
import { tierLabel } from '@/lib/tiers';
import { ApiClientError } from '@/lib/apiClient';
import { fieldAria } from '@/lib/formA11y';
import { useOperatorOrgs, useCreateOrg, useGrantCredit } from './api/operatorHooks';
import { OperatorInstances } from './OperatorInstances';

const PAGE_SIZES = [25, 50, 100] as const;
const selectClass = 'h-8 rounded-md border border-rule bg-transparent px-2 text-[13.5px] text-ink-2';

/** Surfaces any `ApiClientError` with the server's own Romanian message —
 *  unlike `/echipa`'s `messageFor`, both 409 variants here (`operator.ts`'s
 *  duplicate slug / duplicate email) already carry distinct, meaningful
 *  text, so there's no single fixed message to special-case. */
function messageFor(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  return 'A apărut o eroare neașteptată.';
}

/** For the per-row credit-grant failure — same shape as `/echipa`'s
 *  `actionErrorMessage`: any `ApiClientError` shows its own message, other
 *  failures (network, etc.) fall back to the fixed text. */
function actionErrorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiClientError ? err.message : fallback;
}

function RowSkeleton({ cols }: { cols: number }) {
  return (
    <tr className="border-b border-rule-soft">
      <td className="px-4 py-3" colSpan={cols}>
        <div className="h-4 w-full animate-pulse rounded-md bg-wash" aria-hidden />
      </td>
    </tr>
  );
}

const createOrgSchema = z.object({
  name: z.string().trim().min(1, 'Numele este obligatoriu.'),
  slug: z
    .string()
    .trim()
    .min(1, 'Slug-ul este obligatoriu.')
    .regex(/^[a-z0-9-]+$/, 'Slug invalid (doar litere mici, cifre, cratime).'),
  adminEmail: z.string().trim().min(1, 'Introduceți adresa de email').email('Adresă de email invalidă'),
});
type CreateOrgFields = z.infer<typeof createOrgSchema>;

/** Inline (not modal) create-organization form — Direction B, matching
 *  `/echipa`'s `InviteForm`. On success the server has already provisioned
 *  a pending admin-role invitation (no self-signup, no server-generated
 *  password — see `operator.ts`), so the flow here is identical to
 *  `/echipa`'s: show the activation link + a copy button, reset the form. */
function CreateOrgForm() {
  const createMutation = useCreateOrg();
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; link: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateOrgFields>({
    resolver: zodResolver(createOrgSchema),
    defaultValues: { name: '', slug: '', adminEmail: '' },
  });

  async function onSubmit(values: CreateOrgFields) {
    setFormError(null);
    try {
      const res = await createMutation.mutateAsync(values);
      reset();
      setCreated({ email: res.invitation.email, link: res.invitation.link });
      setCopied(false);
    } catch (err) {
      setCreated(null);
      setFormError(messageFor(err));
    }
  }

  async function copyLink() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.link);
      setCopied(true);
    } catch {
      // Clipboard API unavailable/denied — the link stays selectable in the input below.
    }
  }

  return (
    <div className="mt-6">
      <Section title="Organizație nouă">
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="create-org-name">Nume</Label>
            <Input id="create-org-name" autoFocus className="h-8 w-52" {...register('name')} {...fieldAria(!!errors.name, 'create-org-name-error')} />
            {errors.name && <p id="create-org-name-error" className="text-[12.5px] text-bad">{errors.name.message}</p>}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="create-org-slug">Slug</Label>
            <Input id="create-org-slug" className="h-8 w-40 font-mono" {...register('slug')} {...fieldAria(!!errors.slug, 'create-org-slug-error')} />
            {errors.slug && <p id="create-org-slug-error" className="text-[12.5px] text-bad">{errors.slug.message}</p>}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="create-org-email">Email administrator</Label>
            <Input id="create-org-email" type="email" autoComplete="off" className="h-8 w-64" {...register('adminEmail')} {...fieldAria(!!errors.adminEmail, 'create-org-email-error')} />
            {errors.adminEmail && <p id="create-org-email-error" className="text-[12.5px] text-bad">{errors.adminEmail.message}</p>}
          </div>
          <Button type="submit" size="sm" disabled={isSubmitting}>
            {!isSubmitting && <Plus />}
            {isSubmitting ? 'Se creează…' : 'Creează organizație'}
          </Button>
          {formError && <p className="w-full text-[12.5px] text-bad">{formError}</p>}
          {created && (
            <div className="mt-1 flex w-full flex-wrap items-center gap-2 rounded-md border border-rule-soft bg-wash p-2 text-[12.5px]">
              <span className="text-ink-2">
                Organizație creată. Link de activare pentru <strong className="text-ink">{created.email}</strong>:
              </span>
              <Input
                readOnly
                value={created.link}
                onFocus={(e) => e.currentTarget.select()}
                aria-label="Link de activare"
                className="h-7 min-w-[220px] flex-1 font-mono text-[12.5px]"
              />
              <button
                type="button"
                onClick={() => void copyLink()}
                title={copied ? 'Copiat!' : 'Copiază linkul'}
                aria-label={copied ? 'Link copiat' : 'Copiază linkul'}
                className={`flex size-8 shrink-0 items-center justify-center rounded transition-colors ${
                  copied ? 'text-ok' : 'text-ink-3 hover:text-teal'
                }`}
              >
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </button>
            </div>
          )}
        </form>
      </Section>
    </div>
  );
}

const COLS = 7;

export function OperatorOrgsPage() {
  // Uncommitted search input vs. applied search — the query only refetches
  // once search is applied (Enter / „Caută"), matching NomenclatorPage's
  // filter/applied split instead of firing a request on every keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);

  const { data, isPending, isError } = useOperatorOrgs({
    q: appliedSearch,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });
  const grantMutation = useGrantCredit();
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  const list = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // A search/page-size change (or an org disappearing from a search result)
  // can strand `page` past the new `totalPages` — snap back rather than
  // showing a false empty state once the refetch settles.
  useEffect(() => {
    if (!isPending && !isError && total > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [isPending, isError, total, totalPages, page]);

  function applySearch(e: FormEvent) {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setPage(1);
  }

  function resetSearch() {
    setSearchInput('');
    setAppliedSearch('');
    setPage(1);
  }

  function amountFor(id: string): number {
    return Number(amounts[id]);
  }

  function canGrant(id: string): boolean {
    const raw = amounts[id]?.trim();
    if (!raw) return false;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0;
  }

  function handleGrant(org: OperatorOrgSummary) {
    if (!canGrant(org.id)) return;
    grantMutation.mutate(
      { id: org.id, amountEur: amountFor(org.id) },
      { onSuccess: () => setAmounts((prev) => ({ ...prev, [org.id]: '' })) },
    );
  }

  return (
    <>
      <DocTop />
      <div className="mt-8">
        <div>
          <h1 className="text-[32px] font-[650] text-ink">Organizații</h1>
          <p className="mt-1 text-[13.5px] text-ink-3">
            {isPending ? 'Se încarcă…' : `${formatNumberRo(total)} organizații`}
          </p>
        </div>
        <div className="mt-1 border-b-2 border-ink pb-3" />

        <CreateOrgForm />

        <form onSubmit={applySearch} className="mt-6 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="operator-org-search" className="text-[12.5px] text-ink-3">Căutare</Label>
            <Input
              id="operator-org-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Nume sau slug…"
              className="h-8 w-56"
            />
          </div>
          <Button type="submit" variant="outline" size="sm"><Search />Caută</Button>
          {appliedSearch && (
            <button
              type="button"
              onClick={resetSearch}
              className="text-[13.5px] text-ink-3 hover:text-ink-2 hover:underline"
            >
              Resetează căutarea
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Label htmlFor="operator-org-page-size" className="text-[12.5px] text-ink-3">Rezultate/pagină</Label>
            <select
              id="operator-org-page-size"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className={selectClass}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </form>

        <div className="mt-6">
          {isError && <p className="text-[13.5px] text-bad">Nu s-au putut încărca organizațiile.</p>}

          {!isError && isPending && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-[13.5px]">
                <tbody>
                  <RowSkeleton cols={COLS} />
                  <RowSkeleton cols={COLS} />
                  <RowSkeleton cols={COLS} />
                </tbody>
              </table>
            </div>
          )}

          {!isError && !isPending && list.length === 0 && (
            <p className="text-[13.5px] text-ink-3">
              {appliedSearch ? 'Niciun rezultat.' : 'Nicio organizație.'}
            </p>
          )}

          {!isError && !isPending && list.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-[13.5px]">
                <thead>
                  <tr className="border-b border-rule text-left text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3 [&>th]:px-4 [&>th]:py-2.5">
                    <th>Nume</th>
                    <th>Slug</th>
                    <th className="text-right">Membri</th>
                    <th className="text-right">Sold</th>
                    <th>Plan</th>
                    <th>Instanță</th>
                    <th className="text-right">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((org) => {
                    const grantPending = grantMutation.isPending && grantMutation.variables?.id === org.id;
                    const grantFailed = grantMutation.isError && grantMutation.variables?.id === org.id;

                    return (
                      <Fragment key={org.id}>
                        <tr className="border-b border-rule-soft align-top hover:bg-wash">
                          <td className="px-4 py-2 text-[13.5px] text-ink">{org.name}</td>
                          <td className="px-4 py-2 font-mono text-[12.5px] text-ink-2">{org.slug}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-ink-2">{formatNumberRo(org.memberCount)}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-ink-2">{formatCurrencyEurRo(org.balanceEur)}</td>
                          <td className="px-4 py-2 text-ink-2">{tierLabel(org.tierId)}</td>
                          <td className="px-4 py-2">
                            {org.instanceActive ? (
                              <StatusLabel
                                tone="teal"
                                label={`Activă din ${formatDateTimeRo(org.activeSince ?? '')}`}
                                pulse
                              />
                            ) : (
                              <StatusLabel tone="mute" label="Oprită" />
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-end gap-2">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={amounts[org.id] ?? ''}
                                onChange={(e) => setAmounts((prev) => ({ ...prev, [org.id]: e.target.value }))}
                                aria-label={`Sumă credit pentru ${org.name}`}
                                placeholder="EUR"
                                className="h-8 w-24 text-right"
                              />
                              <button
                                type="button"
                                onClick={() => handleGrant(org)}
                                disabled={!canGrant(org.id) || grantPending}
                                className="text-[13.5px] font-medium text-teal hover:underline disabled:pointer-events-none disabled:opacity-50"
                              >
                                {grantPending ? 'Se acordă…' : 'Acordă'}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {grantFailed && (
                          <tr className="border-b border-rule-soft">
                            <td colSpan={COLS} className="px-4 pb-2 pt-0 text-right text-[12.5px] text-bad">
                              {actionErrorMessage(grantMutation.error, 'Acordarea creditului a eșuat.')}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!isError && !isPending && total > 0 && (
          <div className="mt-4 flex items-center justify-between text-[13.5px] text-ink-3">
            <span>
              <span className="font-semibold text-ink">{from}–{to}</span> din{' '}
              <span className="font-semibold text-ink">{formatNumberRo(total)}</span>
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                title="Pagina anterioară"
                aria-label="Pagina anterioară"
                className="flex size-8 items-center justify-center rounded text-ink-2 transition-colors hover:text-ink disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronLeft className="size-4" />
              </button>
              <span className="tabular-nums">Pagina {page} / {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                title="Pagina următoare"
                aria-label="Pagina următoare"
                className="flex size-8 items-center justify-center rounded text-ink-2 transition-colors hover:text-ink disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          </div>
        )}

        <OperatorInstances />
      </div>
    </>
  );
}
